import {
  CreateMySpotInput,
  TopMySpotItemFragment,
  useCreateMySpotMutation,
  useDeleteMySpotMutation,
  useUpdateMySpotMutation,
} from 'generated/graphql'
import { useCustomToast } from 'hooks/useCustomToast'
import { useShowErrorMessageIfNeed } from 'hooks/useShowErrorMessageIfNeed'
import { useCallback } from 'react'

type RefetchTop = () => Promise<unknown>

export const useTopMySpotMutations = (refetch: RefetchTop) => {
  const toast = useCustomToast()
  const { showErrorMessageIfNeed } = useShowErrorMessageIfNeed()
  const [createMySpot, { loading: creating }] = useCreateMySpotMutation({ onError: showErrorMessageIfNeed })
  const [updateMySpot, { loading: updating }] = useUpdateMySpotMutation({ onError: showErrorMessageIfNeed })
  const [deleteMySpot] = useDeleteMySpotMutation({ onError: showErrorMessageIfNeed })

  const refresh = useCallback(async () => {
    try {
      await refetch()
    } catch (error) {
      showErrorMessageIfNeed(error)
    }
  }, [refetch, showErrorMessageIfNeed])

  const create = useCallback(
    async (input: CreateMySpotInput): Promise<TopMySpotItemFragment | undefined> => {
      const response = await createMySpot({ variables: { input } })
      if (response.errors || !response.data?.createMySpot) {
        showErrorMessageIfNeed(response.errors ?? new Error('マイスポットの追加に失敗しました'))
        return
      }
      toast('success', 'マイスポットを追加しました')
      await refresh()
      return response.data.createMySpot
    },
    [createMySpot, refresh, showErrorMessageIfNeed, toast]
  )

  const update = useCallback(
    async (id: string, input: CreateMySpotInput): Promise<TopMySpotItemFragment | undefined> => {
      const response = await updateMySpot({ variables: { input: { id, ...input } } })
      if (response.errors || !response.data?.updateMySpot) {
        showErrorMessageIfNeed(response.errors ?? new Error('マイスポットの更新に失敗しました'))
        return
      }
      toast('success', 'マイスポットを更新しました')
      await refresh()
      return response.data.updateMySpot
    },
    [refresh, showErrorMessageIfNeed, toast, updateMySpot]
  )

  const remove = useCallback(
    async (spot: Pick<TopMySpotItemFragment, 'id'>): Promise<boolean> => {
      const response = await deleteMySpot({ variables: { id: spot.id } })
      if (response.errors || !response.data?.deleteMySpot) {
        showErrorMessageIfNeed(response.errors ?? new Error('マイスポットの削除に失敗しました'))
        return false
      }
      toast('success', 'マイスポットを削除しました')
      await refresh()
      return true
    },
    [deleteMySpot, refresh, showErrorMessageIfNeed, toast]
  )

  return { create, update, remove, creating, updating }
}
